import { createSectionColumnId } from '../../../utils/proposalSectionColumns';
import { createTabId, tabZoneKey } from './tabsLayout';

/**
 * Opérations d'onglet sur l'arbre Puck. Un onglet, ce n'est pas qu'une entrée
 * dans une prop : c'est aussi une zone et le Bloc qu'elle contient. Ajouter,
 * supprimer ou dupliquer un onglet doit donc écrire props ET zones — d'où ces
 * fonctions pures, appelées par un `dispatch({ type: 'setData' })` unique.
 *
 * Sur la création automatique du Bloc : le README (§3) interdit d'insérer des
 * blocs tout seul, avec les macros pour seule exception. Ajouter un onglet est
 * la seconde exception assumée (cf. `docs/webviewer/chantier-onglets.md` §2) —
 * la création est la conséquence directe d'une action explicite, comme le wrap
 * automatique d'une box lâchée à la racine. Rien ne se crée au chargement, ni
 * pour réparer un document.
 */

const zonesOf = (data) => (data?.zones && typeof data.zones === 'object' ? data.zones : {});

/**
 * Bloc 1 colonne, vide. Même forme que `wrapBlockInProposalSection`, à trois
 * réglages près : gouttière, espace inférieur et animation sont mis à zéro et
 * retirés de la sidebar (cf. `nestedSectionFields.js`) — dans un onglet, ils
 * n'ont plus de page à laquelle se référer, et c'est le bloc Onglets qui les
 * porte pour l'ensemble.
 */
export function buildTabSection() {
  const sectionId = `section-tab-${createSectionColumnId()}`;
  const columnId = createSectionColumnId();
  return {
    section: {
      type: 'ProposalSection',
      props: {
        id: sectionId,
        backgroundColor: '',
        paddingTop: 0,
        paddingBottom: 0,
        // Le panneau d'onglet est déjà à la largeur de contenu : une gouttière
        // en plus décalerait le contenu d'un onglet par rapport au reste.
        paddingX: 0,
        marginBottom: 0,
        appearAnimation: 'none',
        isVisible: true,
        sectionColumns: {
          preset: 'row',
          columnIds: [columnId],
          columnSpans: [],
        },
      },
    },
    columnZoneKey: `${sectionId}:col-${columnId}`,
  };
}

/** Toutes les clés de zone du sous-arbre d'un bloc (zones imbriquées incluses). */
function subtreeZoneKeys(zones, rootId) {
  const keys = [];
  const pending = [String(rootId)];
  while (pending.length) {
    const current = pending.shift();
    Object.keys(zones)
      .filter((key) => key.startsWith(`${current}:`))
      .forEach((key) => {
        keys.push(key);
        (zones[key] || []).forEach((block) => {
          const id = block?.props?.id;
          if (id) pending.push(String(id));
        });
      });
  }
  return keys;
}

/**
 * Vide la zone d'un onglet ET, récursivement, celles de ses descendants. Sans
 * ça, les blocs des zones orphelines restent dans le blob : le diff autosave
 * les voit toujours présents et n'émet jamais leur suppression.
 */
function purgeTabZones(zones, blockId, tabId) {
  const rootKey = tabZoneKey(blockId, tabId);
  const doomed = new Set([rootKey]);
  (zones[rootKey] || []).forEach((block) => {
    const id = block?.props?.id;
    if (id) subtreeZoneKeys(zones, id).forEach((key) => doomed.add(key));
  });
  doomed.add(rootKey);
  const next = { ...zones };
  doomed.forEach((key) => { delete next[key]; });
  return next;
}

const newIdFor = (block) => `${String(block?.type || 'block').toLowerCase()}-${createSectionColumnId()}`;

/**
 * Recopie la zone d'un onglet sous une nouvelle clé, ids régénérés sur tout le
 * sous-arbre — deux onglets ne peuvent pas partager un id de bloc (doublon de
 * key React, et surtout deux blocs indissociables pour l'autosave et les
 * verrous). Même contrat que le `duplicate` natif de Puck.
 */
function cloneTabZones(zones, blockId, sourceTabId, targetTabId) {
  const next = { ...zones };
  const walk = (sourceKey, targetKey) => {
    const blocks = zones[sourceKey];
    if (!Array.isArray(blocks)) return;
    next[targetKey] = blocks.map((block) => {
      const sourceId = block?.props?.id;
      const copy = { ...block, props: { ...block.props, id: newIdFor(block) } };
      if (sourceId) {
        Object.keys(zones)
          .filter((key) => key.startsWith(`${sourceId}:`))
          .forEach((key) => {
            const zoneName = key.slice(String(sourceId).length + 1);
            walk(key, `${copy.props.id}:${zoneName}`);
          });
      }
      return copy;
    });
  };
  walk(tabZoneKey(blockId, sourceTabId), tabZoneKey(blockId, targetTabId));
  return next;
}

/** Applique `fn` aux props du bloc `blockId`, où qu'il vive dans l'arbre. */
function mapBlockProps(data, blockId, fn) {
  let touched = false;
  const patch = (block) => {
    if (block?.props?.id !== blockId) return block;
    touched = true;
    return { ...block, props: fn(block.props) };
  };
  const content = Array.isArray(data?.content) ? data.content.map(patch) : data?.content;
  const zones = Object.fromEntries(
    Object.entries(zonesOf(data)).map(([key, blocks]) => [
      key, Array.isArray(blocks) ? blocks.map(patch) : blocks,
    ]),
  );
  return touched ? { ...data, content, zones } : data;
}

/** Props du bloc `blockId`, où qu'il vive dans l'arbre. */
function findBlockProps(data, blockId) {
  const all = [
    ...(Array.isArray(data?.content) ? data.content : []),
    ...Object.values(zonesOf(data)).flatMap((blocks) => (Array.isArray(blocks) ? blocks : [])),
  ];
  return all.find((block) => block?.props?.id === blockId)?.props ?? null;
}

/**
 * `CollapsibleListField` n'annonce pas l'opération, il rend juste la liste
 * suivante : on la déduit des ids.
 *
 * | Ce qu'on lit dans la liste | Opération | Effet sur les zones |
 * | --- | --- | --- |
 * | item sans `id` | ajout | la zone naît avec un Bloc 1 colonne |
 * | `id` déjà vu plus haut | duplication | zone recopiée, ids régénérés |
 * | `id` de l'ancienne liste absent | suppression | zone purgée, récursivement |
 * | mêmes ids, autre ordre | réordonnancement | rien (l'ordre vit dans `tabs`) |
 *
 * @returns {object} nouvel arbre Puck (l'entrée n'est jamais mutée)
 */
export function reconcileTabsChange(data, blockId, nextTabs) {
  const previousIds = new Set(
    (findBlockProps(data, blockId)?.tabs || []).map((tab) => tab?.id).filter(Boolean),
  );

  let zones = { ...zonesOf(data) };
  const seen = new Set();

  const tabs = (nextTabs || []).map((tab) => {
    if (!tab?.id || seen.has(tab.id)) {
      const id = createTabId();
      if (tab?.id && seen.has(tab.id)) {
        zones = cloneTabZones(zones, blockId, tab.id, id);
      } else {
        const { section, columnZoneKey } = buildTabSection();
        zones[tabZoneKey(blockId, id)] = [section];
        zones[columnZoneKey] = [];
      }
      seen.add(id);
      return { ...tab, id };
    }
    seen.add(tab.id);
    return tab;
  });

  previousIds.forEach((id) => {
    if (!seen.has(id)) zones = purgeTabZones(zones, blockId, id);
  });

  return mapBlockProps({ ...data, zones }, blockId, (props) => ({ ...props, tabs }));
}

/**
 * Un onglet est vide quand il ne contient rien de visible : pas de Bloc, ou un
 * Bloc dont aucune colonne n'est occupée. Sert au garde-fou de suppression —
 * un onglet inactif cache son contenu au moment où on clique sur la corbeille.
 */
export function isTabEmpty(data, blockId, tabId) {
  const zones = zonesOf(data);
  const blocks = zones[tabZoneKey(blockId, tabId)] || [];
  if (blocks.length === 0) return true;
  return blocks.every((block) => {
    const id = block?.props?.id;
    if (!id) return true;
    return subtreeZoneKeys(zones, id).every((key) => (zones[key] || []).length === 0);
  });
}

/** L'action Puck est-elle l'insertion d'un bloc Onglets ? */
export function isTabsBlockInsert(action) {
  return action?.type === 'insert' && action.componentType === 'TabsBlock';
}

/**
 * Garnit les onglets d'un bloc fraîchement inséré. Ne touche QUE les onglets
 * dont la zone n'existe pas encore : un Bloc supprimé par l'utilisateur laisse
 * une zone vide mais présente, et ne doit pas repousser.
 */
export function ensureTabsBlockSections(data, blockId) {
  const props = findBlockProps(data, blockId);
  if (!props) return data;

  const zones = { ...zonesOf(data) };
  let changed = false;
  (props.tabs || []).forEach((tab) => {
    if (!tab?.id) return;
    const key = tabZoneKey(blockId, tab.id);
    if (key in zones) return;
    const { section, columnZoneKey } = buildTabSection();
    zones[key] = [section];
    zones[columnZoneKey] = [];
    changed = true;
  });

  return changed ? { ...data, zones } : data;
}
